import i18n from '@/utils/i18n';
import ProspectImagePlaceholder from '@assets/icons/prospect-image-placeholder.svg';
import React, { useCallback } from 'react';
import { Badge, Button, Icons, Typography } from '@usepepper/sprout-kit-ui';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { distanceByCountryCode } from '@/utils/distance';
import { ImperativeOverlayView } from '@usepepper/ui/molecules/map/imperative-overlay-view';
import { MAP_Z_INDEX } from '@usepepper/ui/molecules/map/constants';
import { NearbyBusiness } from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';
import { Pressable, View } from 'react-native';

const CARD_WIDTH = 320;
const BANNER_HEIGHT = 120;
// Matches the MapMarker pin size so the card clears the pin.
const PIN_HEIGHT = 32;
const PIN_GAP = 8;

// The OverlayView container sits at the pin coordinate; anchor the card
// centered above the pin with fixed offsets (the width is known).
const cardContainerStyle: React.CSSProperties = {
  bottom: PIN_HEIGHT + PIN_GAP,
  left: -CARD_WIDTH / 2,
  position: 'absolute',
  zIndex: MAP_Z_INDEX.pinCard,
};

interface Props {
  business: NearbyBusiness;
  claiming?: boolean;
  onClaim: (business: NearbyBusiness) => void;
  onClose: () => void;
  onViewDetails?: (business: NearbyBusiness) => void;
  supplierCountryCode?: string;
}

export const BusinessPinCard: React.FC<Props> = ({
  business,
  claiming,
  onClaim,
  onClose,
  onViewDetails,
  supplierCountryCode,
}) => {
  const [styles, theme] = useStyles();
  const isExisting = !!business.restaurantUuid;

  // Native-level guard: React's delegated onClick fires too late to stop
  // the click from reaching Google Maps' own listeners.
  const preventMapHits = useCallback((node: HTMLDivElement | null) => {
    if (node && typeof google !== 'undefined') {
      google.maps.OverlayView.preventMapHitsAndGesturesFrom(node);
    }
  }, []);

  const meta = [
    ...(business.distance !== null && business.distance !== undefined
      ? [distanceByCountryCode(business.distance, supplierCountryCode)]
      : []),
    ...(business.categories?.length ? [business.categories[0]] : []),
  ];

  return (
    <ImperativeOverlayView
      position={{
        lat: business.latitude,
        lng: business.longitude,
      }}
    >
      {/* Plain div so card clicks can't bubble to the map's onClick. */}
      <div
        aria-label={business.businessName}
        onClick={(event) => event.stopPropagation()}
        ref={preventMapHits}
        role="dialog"
        style={cardContainerStyle}
      >
        <View style={styles.card}>
          <View style={styles.banner}>
            {business.imageUrl ? (
              <CachedImage
                accessibilityLabel={i18n.t('business_image', {
                  businessName: business.businessName,
                })}
                contentFit="cover"
                source={{ uri: business.imageUrl }}
                style={styles.bannerImage}
              />
            ) : (
              <ProspectImagePlaceholder
                accessibilityLabel={i18n.t('business_image_placeholder', {
                  businessName: business.businessName,
                })}
                height={BANNER_HEIGHT}
                width={CARD_WIDTH}
              />
            )}
            <Pressable
              accessibilityLabel={i18n.t('close')}
              onPress={onClose}
              style={styles.closeButton}
            >
              <Icons.X color={theme.sk.color.icon.primary} size={16} />
            </Pressable>
          </View>
          <View style={styles.content}>
            <Typography bold numberOfLines={1} size="$sm">
              {business.businessName}
            </Typography>
            {business.displayAddress ? (
              <Typography color="$textSecondary" size="$xs">
                {business.displayAddress}
              </Typography>
            ) : null}
            {meta.length > 0 ? (
              <Typography color="$textSecondary" size="$xs">
                {meta.join(' • ')}
              </Typography>
            ) : null}
            <View style={styles.actionSlot}>
              {isExisting ? (
                <View style={styles.badgeRow}>
                  <Badge color="muted">
                    {i18n.t('sales_portal.prospect_finder.row.existing_badge')}
                  </Badge>
                </View>
              ) : (
                <View style={styles.actionItem}>
                  <Button
                    loading={!!claiming}
                    onPress={() => onClaim(business)}
                    size="$sm"
                    variant="primary"
                    width="100%"
                  >
                    {i18n.t('sales_portal.prospect_finder.row.claim_button')}
                  </Button>
                </View>
              )}
              {isExisting && onViewDetails ? (
                <View style={styles.actionItem}>
                  <Button
                    onPress={() => onViewDetails(business)}
                    size="$sm"
                    variant="secondary"
                    width="100%"
                  >
                    {i18n.t(
                      'sales_portal.prospect_finder.row.view_details_button',
                    )}
                  </Button>
                </View>
              ) : null}
            </View>
          </View>
        </View>
      </div>
    </ImperativeOverlayView>
  );
};

const useStyles = createStyles((theme) => ({
  actionItem: {
    flex: 1,
  },
  actionSlot: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
    marginTop: theme.sk.spacing.spaceSmall,
  },
  badgeRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  banner: {
    height: BANNER_HEIGHT,
    overflow: 'hidden',
  },
  bannerImage: {
    height: BANNER_HEIGHT,
    width: CARD_WIDTH,
  },
  card: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.16)',
    overflow: 'hidden',
    width: CARD_WIDTH,
  },
  closeButton: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusRound,
    height: 28,
    justifyContent: 'center',
    position: 'absolute',
    right: theme.sk.spacing.spaceSmall,
    top: theme.sk.spacing.spaceSmall,
    width: 28,
  },
  content: {
    gap: theme.sk.spacing.spaceXsmall,
    padding: theme.sk.spacing.spaceMedium,
  },
}));
